import * as Color from "color"
import PptxGenJS, { Slide, ChartOptions, ChartBarDataset,ChartMultiTypeItemOptions, ChartMultiTypeOptions,	ChartMultiTypeItem} from "pptxgenjs"
import { Decomposition } from "../debt-table"
import * as DebtUtils from "../utils"
import { ReportConfiguration } from "../../../configuration"
import { MasterSlide } from "../../pptx-configuration/master-slides"
import {
	getPlaceholderChartBodyOptions,
	Placeholder
} from "../../pptx-configuration/placeholders"
import { DocumentSection } from "../../types"
import { formatTitle } from "../../utils"
import { Loan, LoanDecomposition, LoanWithDecomposition, LocalAuthority, Ppi, Scenario } from "../../../../../model"
import { getChartBaseOptions, getChartColors } from "../../pptx-configuration/charts"
import * as Constants from "../../pptx-configuration/constants"
import { ColorsHelper } from "../../../../../util/colors"
import * as CashFlowDomain from "../../../../../domain/sfp/cash-flow-domain"
import * as Tabledefinition from "./../../../../scenarii/detail/table-definition"
import Operations from "../../../../scenarii/common/operations"
import * as LoanDomain from "../../../../../domain/sfp/loan-domain"

export function checkRemainingCapitalSection(
	configuration: ReportConfiguration
): boolean {
	const remainingCapital = configuration.debt.remainingCapital
	return remainingCapital.globalCharts
	|| remainingCapital.perBank
	|| remainingCapital.perRateType
	|| remainingCapital.tables
}

export function createRemainingCapitalSection(
	pptx: PptxGenJS,
	configuration: ReportConfiguration,
	numbering: number[],
	decomposedLoans: LoanWithDecomposition[],
	years: number[],
	scenario:Scenario,
	ppi:Ppi,
	loans:any,
	localAuthority:LocalAuthority,
	loanDecompositions:LoanDecomposition[]
): DocumentSection {
	const slides: Slide[] = []
	const slidesTitle = formatTitle("Capital restant dû", numbering)
	const remainingCapital = configuration.debt.remainingCapital
	const placeholderBody = getPlaceholderChartBodyOptions()
	const baseColor = new Color(configuration.table.header.backgroundColor)

	if (remainingCapital.tables) {
		DebtUtils.includeDebtTable(
			pptx,
			slidesTitle,
			"management-report-debt-remaining-capital",
			Decomposition.RemainingCapital,
			decomposedLoans,
			years,
			configuration
		)
	}

	if (remainingCapital.globalCharts) {
		const slide = pptx.addNewSlide(MasterSlide.Chart)

		slide.addText(slidesTitle, {
			placeholder: Placeholder.Title
		})

		const chartData = DebtUtils.createChartData(
			Decomposition.RemainingCapital,
			decomposedLoans,
			years,
			localAuthority,
			loans,
			scenario,
			ppi
		)
		
		let txDesData = getDataTauxDesendettement(years,scenario,localAuthority,ppi,loans,loanDecompositions,decomposedLoans);
		const txDes:[ChartBarDataset] = [{
			name:"Taux de désendettement (en années)",
			values:txDesData,
			labels:["Taux de désendettement (en années)"],
		}];
		const globalTxDesendettementTitle = "Capital restant dû et taux de désendettement par annuité"
		const globalPerInhabitantOptions: ChartMultiTypeOptions = {
			...getChartBaseOptions(placeholderBody, null),
			title: globalTxDesendettementTitle,
			catAxes: [{}, { catAxisHidden: true }],
			valAxes: [
				{ valAxisLabelFormatCode: Constants.STRING_FORMAT_CURRENCY },
				{
					valAxisLabelFormatCode: "#,##0",
					valAxisLineShow: true,
					valGridLine: "none"
				}
			],
			chartColors:getChartColors(),
		} as ChartMultiTypeOptions

		const populationChartOptions: ChartMultiTypeItemOptions = {
			chartColors: ["666666"],
			chartColorsOpacity: 50,
			lineSize: 1,
			secondaryCatAxis: true,
			secondaryValAxis: true,
			showLegend:true,
		}

		const globalPerInhabitantCharts: ChartMultiTypeItem[] = [
			{ data: chartData, type: pptx.charts.BAR ,options:{barGrouping:"stacked"}},
			{ data: txDes,options: populationChartOptions,type: pptx.charts.LINE}
		]

		slide.addChart(globalPerInhabitantCharts,globalPerInhabitantOptions);

		slides.push(slide)
	}

	if (remainingCapital.PieChartType) {
		const slide = pptx.addNewSlide(MasterSlide.Chart)

		slide.addText(slidesTitle, {
			placeholder: Placeholder.Title
		})

		const pieChartData = DebtUtils.createPieChartData(
			decomposedLoans,
			"remaining",
			configuration.generalities.referenceYear
		)

		const pieChartOptions = DebtUtils.createPieChartOptions(
			pieChartData,
			placeholderBody,
			baseColor
		)

		slide.addChart(pptx.charts.PIE, pieChartData, pieChartOptions)
		slides.push(slide)
	}

	if (remainingCapital.perBank || remainingCapital.perRateType) {
		const slide = pptx.addNewSlide(MasterSlide.Chart)

		slide.addText(slidesTitle, {
			placeholder: Placeholder.Title
		})

		if (remainingCapital.perBank) {
			const perBankChartData = DebtUtils.createPerBankChartData(
				decomposedLoans,
				"remaining",
				configuration.generalities.referenceYear
			)

			const perBankChartOptions = DebtUtils.createPerBankChartOptions(
				perBankChartData,
				remainingCapital.perRateType,
				placeholderBody,
				baseColor
			)

			slide.addChart(pptx.charts.PIE, perBankChartData, perBankChartOptions)
		}

		if (remainingCapital.perRateType) {
			const perRateTypeChartData = DebtUtils.createPerRateTypeChartData(
				decomposedLoans,
				"remaining",
				configuration.generalities.referenceYear
			)

			const perRateTypeChartOptions = DebtUtils.createPerRateTypeChartOptions(
				remainingCapital.perBank,
				placeholderBody,
				baseColor
			)

			slide.addChart(
				pptx.charts.PIE,
				perRateTypeChartData,
				perRateTypeChartOptions
			)
		}

		slides.push(slide)
	}

	return { slides, title: slidesTitle }
}

function getDataTauxDesendettement(years:number[], scenario:Scenario, localAuthority:LocalAuthority, ppi:Ppi, loans:any, loanDecompositions:LoanDecomposition[], decomposedLoans:LoanWithDecomposition[]):number[]{
	var currentYear = new Date().getFullYear()
	const grossCashFlows = [];
	const remainingCapitals = [];
	const getOperations = (): Operations => {
		return new Operations(scenario, ppi, localAuthority)
	}
	const simulatedTotals = LoanDomain.getSimulatedLoansTotalsByYearFromDecomposed(decomposedLoans, years);
	years.forEach(y => {
		if (localAuthority.isDebtSimplified) {
			// CRD en cours : saisi. CRD des emprunts simulés : repris de la dette détaillée.
			// On ne conditionne pas sur l'existence de la ligne en base, sinon une année
			// sans ligne vaudrait 0 ici alors que le PPI et l'écran de dette simplifiée
			// y affichent le CRD simulé : les chiffres doivent concorder partout.
			const simplifiedYear = (localAuthority.localAuthorityDebtSimplifiedYears || []).find((sy: any) => sy.year === y);
			const crdEnCours = simplifiedYear && simplifiedYear.amount != null ? simplifiedYear.amount : 0;
			const crdSimule = LoanDomain.getSimulatedLoanTotals(simulatedTotals, y).remainingCapital;
			remainingCapitals.push(crdEnCours + crdSimule);
		} else {
			remainingCapitals.push(
				LoanDomain.getTotalYearRemainingCapital(loanDecompositions, y));
		}
		if(scenario){
			let type: any = y < currentYear ? "retrospective" : "prospective"
		// CAF brute
		grossCashFlows.push(Tabledefinition._aggregateCAFBrute(
			type, y, localAuthority, getOperations(), null, [], loans));		
		}else{
			grossCashFlows.push(0)
		}		
	})	
	return CashFlowDomain.calculateCashFlowDeleveragingYears(grossCashFlows, remainingCapitals)
}


// WEBPACK FOOTER //
// ./src/component/management-report/generator/debt-generator/remaining-capital-generator/index.tsx